import type { Entry } from "./api";
import { whenLabel } from "./dates";

/**
 * La mise en page de la frise, en fonctions pures (spec, section 3.3) : les memes points donnent
 * le fil horizontal du bureau et le fil vertical du telephone.
 *
 * Horizontal : un axe du temps de la naissance (ou du premier point) a aujourd'hui, chaque point
 * date a sa place, puis une hauteur par point, au-dessus ou en dessous du fil, pour que les boules
 * et leurs libelles ne se chevauchent pas. Vertical : la liste dans l'ordre, sans echelle.
 */

const DAY_MS = 86_400_000;

/** Une date `AAAA-MM-JJ` en millisecondes UTC. */
export const toMs = (iso: string): number => {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
};

/** Les points dates, dans l'ordre de leur debut (le plus ancien d'abord), puis par identifiant. */
export function datedInOrder(entries: readonly Entry[]): Entry[] {
  return entries
    .filter((entry) => !entry.undated && entry.startFrom !== null)
    .sort(
      (a, b) =>
        (a.startFrom as string).localeCompare(b.startFrom as string) ||
        (a.startTo ?? "").localeCompare(b.startTo ?? "") ||
        a.id - b.id,
    );
}

/** Les points « a dater », dans l'ordre recu. */
export const undatedEntries = (entries: readonly Entry[]): Entry[] =>
  entries.filter((entry) => entry.undated || entry.startFrom === null);

export type AxisRange = { readonly from: string; readonly to: string };

/**
 * L'etendue de l'axe : du 1er janvier de l'annee de naissance (ou du premier point, s'il precede
 * la naissance ou s'il n'y a pas de date de naissance) jusqu'a aujourd'hui, ou jusqu'a la derniere
 * date connue si un point est dans le futur. Sans rien, les douze derniers mois.
 */
export function axisRange(
  entries: readonly Entry[],
  birthDate: string | null,
  today: string,
): AxisRange {
  const dated = datedInOrder(entries);
  const starts = dated.map((entry) => entry.startFrom as string);
  if (birthDate !== null) starts.push(birthDate);
  const firstYear =
    starts.length === 0
      ? Number(today.slice(0, 4)) - 1
      : Number(starts.sort((a, b) => a.localeCompare(b))[0]?.slice(0, 4));
  let to = today;
  for (const entry of dated) {
    for (const value of [entry.startTo, entry.endTo]) {
      if (value !== null && value > to) to = value;
    }
  }
  const from = `${String(firstYear).padStart(4, "0")}-01-01`;
  return { from, to: to > from ? to : today };
}

/** La place d'une date sur l'axe, de 0 (debut) a 1 (fin), bornee. */
export function fraction(iso: string, range: AxisRange): number {
  const start = toMs(range.from);
  const span = Math.max(toMs(range.to) - start, DAY_MS);
  return Math.min(1, Math.max(0, (toMs(iso) - start) / span));
}

/** Le milieu de deux dates `AAAA-MM-JJ`, en millisecondes. */
const middleMs = (from: string, to: string): number => (toMs(from) + toMs(to)) / 2;

export type Tick = { readonly year: number; readonly x: number };

/**
 * Les reperes d'annees : un pas de 1, 2, 5, 10 ou 20 ans, le plus petit qui laisse au moins
 * `minGap` pixels entre deux reperes.
 */
export function yearTicks(range: AxisRange, width: number, minGap = 70): Tick[] {
  const first = Number(range.from.slice(0, 4));
  const last = Number(range.to.slice(0, 4));
  const years = Math.max(last - first, 1);
  const step = [1, 2, 5, 10, 20, 50].find((s) => (width / years) * s >= minGap) ?? 50;
  const ticks: Tick[] = [];
  for (let year = Math.ceil(first / step) * step; year <= last; year += step) {
    ticks.push({ year, x: fraction(`${String(year).padStart(4, "0")}-01-01`, range) * width });
  }
  return ticks;
}

/** Largeur estimee du libelle a cote d'une boule (titre 13 px, date 11 px), bornee. */
export const LABEL_MAX = 180;
export function labelWidth(entry: Entry): number {
  const title = entry.title.length * 7;
  const when = whenLabel(entry).length * 6;
  return Math.min(LABEL_MAX, Math.max(title, when, 24));
}

/** L'emprise horizontale d'un element : son bord gauche et sa largeur, en pixels. */
export type Span = { readonly left: number; readonly width: number };

/**
 * Une hauteur par element, au-dessus du fil (1, 2...) ou en dessous (-1, -2...), pour que deux
 * elements sur la meme rangee ne se touchent pas. Glouton, de gauche a droite : chaque element
 * prend la premiere rangee libre dans l'ordre 1, -1, 2, -2..., `maxLevels` de chaque cote. Si
 * aucune n'est libre, il prend celle qui se libere le plus tot (chevauchement le plus court).
 * Rend les rangees dans l'ordre des elements recus.
 */
export function assignLevels(spans: readonly Span[], maxLevels = 4, gap = 12): number[] {
  const order: number[] = [];
  for (let level = 1; level <= maxLevels; level += 1) order.push(level, -level);
  const rightEdge = new Map<number, number>(
    order.map((level) => [level, Number.NEGATIVE_INFINITY]),
  );
  const levels = new Array<number>(spans.length).fill(1);
  const byLeft = spans
    .map((span, index) => ({ span, index }))
    .sort((a, b) => a.span.left - b.span.left);
  for (const { span, index } of byLeft) {
    let chosen = order.find((level) => (rightEdge.get(level) as number) + gap <= span.left);
    if (chosen === undefined) {
      chosen = order.reduce((best, level) =>
        (rightEdge.get(level) as number) < (rightEdge.get(best) as number) ? level : best,
      );
    }
    levels[index] = chosen;
    rightEdge.set(chosen, span.left + span.width);
  }
  return levels;
}

export type PlacedEntry = {
  readonly entry: Entry;
  /** Le centre de la boule, en pixels depuis le debut de l'axe. */
  readonly x: number;
  /** Sa rangee : positive au-dessus du fil, negative en dessous. */
  readonly level: number;
  /** La bande d'une periode le long du fil, ou `null` pour un moment. */
  readonly band: { readonly from: number; readonly to: number } | null;
  /** L'intervalle a eclairer sur le fil quand la carte est ouverte. */
  readonly highlight: { readonly from: number; readonly to: number };
  /** Le cote du libelle : a droite de la boule, sauf contre le bord droit de l'axe. */
  readonly labelSide: "left" | "right";
};

/** Demi-cible de la boule (44 px) et ecart boule-libelle, comme la planche. */
const HIT_HALF = 22;
const BALL_HALF = 10;
const LABEL_GAP = 10;

/**
 * Place les points dates sur un axe de `width` pixels. Un moment se place au milieu de son
 * intervalle ; une periode a son debut, avec une bande jusqu'a sa fin (aujourd'hui si elle est en
 * cours). Les « a dater » n'y sont pas.
 */
export function placeHorizontal(
  entries: readonly Entry[],
  range: AxisRange,
  width: number,
  today: string,
): PlacedEntry[] {
  const dated = datedInOrder(entries);
  const xOfMs = (ms: number) => {
    const start = toMs(range.from);
    const span = Math.max(toMs(range.to) - start, DAY_MS);
    return Math.min(1, Math.max(0, (ms - start) / span)) * width;
  };
  const xOf = (iso: string) => xOfMs(toMs(iso));
  const geometry = dated.map((entry) => {
    const startFrom = entry.startFrom as string;
    const startTo = entry.startTo ?? startFrom;
    if (entry.kind === "period") {
      const end = entry.ongoing ? today : (entry.endTo ?? startTo);
      const band = { from: xOf(startFrom), to: Math.max(xOf(end), xOf(startFrom)) };
      return { entry, x: band.from, band, highlight: band };
    }
    const highlight = { from: xOf(startFrom), to: xOf(startTo) };
    return { entry, x: xOfMs(middleMs(startFrom, startTo)), band: null, highlight };
  });
  const sided = geometry.map((item) => {
    const label = BALL_HALF + LABEL_GAP + labelWidth(item.entry);
    const labelSide: "left" | "right" = item.x + label > width ? "left" : "right";
    const span =
      labelSide === "right"
        ? { left: item.x - HIT_HALF, width: HIT_HALF + label }
        : { left: item.x - label, width: label + HIT_HALF };
    return { item, labelSide, span };
  });
  const levels = assignLevels(sided.map(({ span }) => span));
  return sided.map(({ item, labelSide }, index) => ({
    ...item,
    labelSide,
    level: levels[index] ?? 1,
  }));
}

/** Largeur de l'axe : au moins `minWidth`, et 28 px par annee pour une vie longue. */
export function axisWidth(range: AxisRange, minWidth: number): number {
  const years = (toMs(range.to) - toMs(range.from)) / (365.25 * DAY_MS);
  return Math.max(minWidth, Math.round(years * 28));
}

/** La hauteur d'une rangee et les marges du dessin. */
export const ROW_STEP = 56;
const TOP_MARGIN = 40;
const BOTTOM_MARGIN = 48;

/** La place du fil et la hauteur totale, selon les rangees occupees (au moins une de chaque cote). */
export function verticalExtent(levels: readonly number[]): { threadY: number; height: number } {
  const up = Math.max(1, ...levels.filter((level) => level > 0));
  const down = Math.max(1, ...levels.filter((level) => level < 0).map((level) => -level));
  const threadY = TOP_MARGIN + up * ROW_STEP;
  return { threadY, height: threadY + down * ROW_STEP + BOTTOM_MARGIN };
}

/** Le cote du libelle sur le fil vertical : le premier a droite, puis en alternance. */
export const verticalSide = (index: number): "left" | "right" =>
  index % 2 === 0 ? "right" : "left";
